import React, { useEffect, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Box, Button, MenuItem } from "@mui/material";
import {
  addEmissionFactorSetting,
  checkExistingSite,
  getCustomSiteType,
  getEmissionFactorSetting
} from "api";
import { EsgDropDownIcon } from "assets";
import { useNotify } from "hooks";
import { COLORS, TYPOGRAPHY_COMMON_STYLES } from "components";

import { CustomSelect } from "./custom-select";
import { CustomSmallOutlinedInput } from "./modal";
import { EmissionFactorSetting } from "./types";

export const EmissionFactorSettings = () => {
  const notify = useNotify();

  const [selectedEmissionFactorSetting, setSelectedEmissionFactorSetting] = useState<any>();

  const { data: emissionFactorSettingData = [] } = useQuery(
    ['emissionFactorSettings'],
    getEmissionFactorSetting
  );

  const { data: isSiteExist = true } = useQuery(
    ['isSiteExist'],
    checkExistingSite
  );

  useQuery(['customSiteType'], getCustomSiteType);

  const { mutateAsync: setEmissionFactorSetting } = useMutation(addEmissionFactorSetting);

  const handleEmissionFactorSetting = (event: any) => {
    const selectedValue = event.target.value;
    const selectedItem = emissionFactorSettingData.find(
      (item: EmissionFactorSetting) => item.settingName === selectedValue
    );
    if (selectedItem) {
      const { id, settingName: value } = selectedItem;
      setSelectedEmissionFactorSetting({ id, value });
    }
  };

  const handleEmissionFactorSettingClick = () => {
    const { id, value }: any = selectedEmissionFactorSetting;
    setEmissionFactorSetting(
      { id, settingName: value },
      {
        onError: () =>
          notify.error("Some error has happened while updating emission factor"),
        onSuccess: () => {
          notify.success("Successfully updated Emission Factor Setting");
        }
      }
    );
  };

  useEffect(() => {
    const activeSetting = emissionFactorSettingData.find((item: EmissionFactorSetting) => item.active === true);
    if (activeSetting) {
      setSelectedEmissionFactorSetting({ id: activeSetting.id, value: activeSetting.settingName });
    }
  }, [emissionFactorSettingData]);

  if (!selectedEmissionFactorSetting) return null;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        my: 2,
      }}
    >
      <Box>
        <CustomSelect
          IconComponent={EsgDropDownIcon}
          disabled={isSiteExist}
          value={selectedEmissionFactorSetting?.value}
          onChange={handleEmissionFactorSetting}
          input={<CustomSmallOutlinedInput />}
          sx={{ width: "240px" }}
          displayEmpty
        >
          {emissionFactorSettingData.map((item: EmissionFactorSetting) => (
            <MenuItem
              id={String(item?.id)}
              key={item?.id}
              value={item?.settingName}
              sx={{
                fontWeight: TYPOGRAPHY_COMMON_STYLES.fontWeightSemiBold,
                fontSize: 14,
                lineHeight: "18px",
                color: '#90959E',
                '&:hover': {
                  backgroundColor: '#F6F8FA',
                  borderRadius: 5,
                  color: COLORS.Romance,
                },
                '&.Mui-selected': {
                  backgroundColor: '#F6F8FA',
                  borderRadius: 5,
                  color: COLORS.Romance,
                },
              }}
            >
              {item?.settingName}
            </MenuItem>
          ))}
        </CustomSelect>
      </Box>
      <Box>
        <Button
          onClick={handleEmissionFactorSettingClick}
          variant="contained"
          disabled={isSiteExist}
          sx={{
            color: "white",
            backgroundColor: COLORS.Romance,
            width: "242px",
            height: "36px",
            borderRadius: '8px'
          }}
        >
          Apply
        </Button>
      </Box>
    </Box>
  );
};
